import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import debug from 'debug';

import { createEventEmitter } from '@reckon-web/events';

import { DefaultLoader } from './DefaultLoader';
import type {
  FeatureFlagContextContext,
  FeatureFlagSubscribeHandlerParameters,
  LDClientLoadingState,
  ReckonUserAttributes,
} from './types';
import { createAnonymousUser } from './createAnonymousUser';
import { createAuthenticatedUser } from './createAuthenticatedUser';

function createDisplayName(name: string) {
  const [first, ...rest] = name.toString();
  return (
    [first.toUpperCase(), rest.join('').toLowerCase()].join('') +
    'FeatureFlagsProvider'
  );
}

export type FeatureFlagProviderEvents<Flags> = {
  flagsUpdated: (flags: Flags) => void;
};

export function createFeatureFlagProvider<Flags extends object>(
  name: string,
  clientSideID: string,
  activeFlags: Flags,
  Context: FeatureFlagContextContext<Flags>
) {
  const eventEmitter = createEventEmitter<FeatureFlagProviderEvents<Flags>>(
    'featureFlagEvents'
  );

  const displayName = createDisplayName(name);
  const log = debug(displayName);

  if (typeof window === 'undefined') {
    return ({ loader }: PropsWithChildren<{ loader?: ReactNode }>) => {
      return <>{loader || <DefaultLoader />}</>;
    };
  }

  const client = LaunchDarkly.initialize(
    clientSideID,
    { kind: 'user', anonymous: true },
    {
      privateAttributes: [
        'email',
        'firstName',
        'country',
        'lastName',
        'avatar',
        'name',
        'ip',
      ],
      // Disables sending analytics for the automatic allFlags() call LD does on identify()
      sendEventsOnlyForVariation: true,
    }
  );

  const Provider = ({
    children,
    loader,
    defaultUser,
    localFeatureFlagOverrides,
  }: PropsWithChildren<{
    loader?: ReactNode;
    defaultUser?: ReckonUserAttributes;
    localFeatureFlagOverrides?: Flags;
  }>) => {
    const [loadingState, setLoadingState] = useState<LDClientLoadingState>(
      'loading'
    );
    const [error, setError] = useState<string>();
    const [flags, setFlags] = useState<Flags>({
      ...activeFlags,
      ...localFeatureFlagOverrides,
    });

    const [user, setUser] = useState<ReckonUserAttributes | undefined>(
      defaultUser
    );

    const userHash = JSON.stringify(user);

    const activeFlagNames = useMemo(() => {
      const output: (keyof Flags)[] = [];

      for (const item of Object.entries(flags)) {
        const [flag, enabled] = item;

        if (!enabled) {
          continue;
        }

        output.push(flag as keyof Flags);
      }

      return output;
    }, [flags]);

    const updateFlags = (newFlags: Flags) => {
      setFlags((flags) => {
        const output = {
          ...flags,
          ...newFlags,
          ...localFeatureFlagOverrides,
        };
        log('provider.client.updateFlags', output);
        return output;
      });
    };

    const setLDUser = async (user?: ReckonUserAttributes) => {
      if (!user) {
        return;
      }

      await client.waitUntilReady();
      if (user.anonymous) {
        await client.identify({
          kind: 'user',
          anonymous: true,
          host: user.host,
        });
      } else {
        const { anonymous, ...data } = user;
        await client.identify({
          kind: 'user',
          key: data.user_id,
          ...data,
        });
      }
    };

    /**
     * Subscribe to changes
     * - if a tag is provided, then only that tags changes will trigger the handler
     * - otherwise the handler will recieve events for all flags and streaming mode will be enabled.
     * - if no handler is provided we run sync
     */
    const subscribe = useCallback(
      async ({
        flag,
        handler,
      }: FeatureFlagSubscribeHandlerParameters<Flags> = {}) => {
        await client.waitForInitialization();

        client.on(
          // tag can be a symbol, which we don't want
          `change${
            !!flag && typeof flag !== 'string' ? `:${String(flag)}` : ''
          }`,
          // if no handler provided mock one, because simply watching for changes
          // will cause LaunchDarkly client to stream updates to flags.
          typeof handler === 'function' ? handler : updateFlags
        );
      },
      []
    );

    useEffect(() => {
      async function syncUserAndFlags() {
        if (!user) {
          setUser(createAnonymousUser);
          return;
        }

        log('provider.client.syncingFlags');
        try {
          await client.waitUntilReady();
          await setLDUser(user);

          await client.waitUntilReady();

          const evaluatedFlags: Record<string, any> = {};
          /**
           * Only evaluate flags that are present in code so that LD analytics can
           * tell us which flags are not being used anymore.
           */
          Object.entries(activeFlags).forEach(([key, value]) => {
            evaluatedFlags[key] = client.variation(key, value);
          });
          updateFlags(evaluatedFlags as Flags);
          eventEmitter.broadcast('flagsUpdated', [evaluatedFlags as Flags]);
          log('provider.client.syncedFlags');

          setLoadingState('ready');
        } catch (error) {
          log('provider.client.errorSyncingFlags');
          if (error instanceof Error) {
            setError(error.message);
            setLoadingState('error');
          }
        }
      }

      syncUserAndFlags();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      //user -- Using userHash to reduce unnecessary re-renders. If you're still getting re-renders for the same user then your order of props is probably different.
      userHash,
    ]);

    /**
     *  Render the provided loader
     */
    if (loadingState === 'loading' && loader) {
      return <>{loader}</>;
    }

    /**
     * Filthy peasants can't provide their own loader.... sigh
     */
    if (loadingState === 'loading' && !loader) {
      return <DefaultLoader />;
    }

    /**
     * Welcome to the utopian land of feature flags.
     */
    return (
      <Context.Provider
        value={{
          _client: client,
          flags,
          activeFlagNames,
          error,
          loadingState,
          setUser: async (data) => {
            return setUser(createAuthenticatedUser(data));
          },
          unsetUser: async () => {
            return setUser(createAnonymousUser());
          },
          subscribe,
        }}
      >
        {children}
      </Context.Provider>
    );
  };

  log(`provider.create`, displayName);
  Provider.displayName = displayName;

  return Provider;
}
